import { useState } from 'react';
import { Link } from 'react-router';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Spinner } from '@/components/ui/spinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/sonner';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import {
	useToolDefinitions,
	useToolPreconditions,
	useUpdatePrecondition,
	useDeletePrecondition,
	useSetPreconditionCombinator
} from '@/hooks';
import { usePermissions } from '@/hooks/use-permissions';
import { GLOBAL_PRECONDITION_SCOPE } from '@/services';
import type { PreconditionCombinator, ToolPrecondition } from '@/services';
import { PILL_BUTTON } from '@/modules/social-agent/ui-chrome';
import { PreconditionFormDialog } from './precondition-form-dialog';

const SOURCE_LABEL: Record<ToolPrecondition['source'], string> = {
	input: 'input',
	customer: 'customer',
	order: 'order'
};

const OPERATOR_LABEL: Record<ToolPrecondition['operator'], string> = {
	eq: '=',
	neq: '≠',
	lt: '<',
	lte: '≤',
	gt: '>',
	gte: '≥',
	in: 'in',
	exists: 'exists'
};

function describe(rule: ToolPrecondition): string {
	const field = rule.source === 'input' ? rule.field : `${SOURCE_LABEL[rule.source]}.${rule.field}`;
	if (rule.operator === 'exists') return `${field} exists`;
	const value = Array.isArray(rule.value) ? rule.value.join(', ') : String(rule.value ?? '');
	return `${field} ${OPERATOR_LABEL[rule.operator]} ${value}`;
}

interface PreconditionsSectionProps {
	provider: string;
	tool: string;
}

/** Low risk runs without approval. A missing riskLevel counts as needing approval, as in Brahma. */
const runsWithoutApproval = (riskLevel?: string) => ['low', 'auto'].includes(riskLevel?.toLowerCase() ?? '');

/** Account-wide rules, read-only, under an action's own: the action runs on its own only when
 * both groups pass. A low-risk action is only checked against them once it has rules of its own. */
function AccountWidePreconditions({ enabled, applies }: { enabled: ToolPrecondition[]; applies: boolean }) {
	const combinator = enabled[0]?.combinator ?? 'all';

	return (
		<div className='flex flex-col gap-2 border-t border-gray-300 pt-4'>
			<div className='flex items-center justify-between gap-3'>
				<p className='text-sm font-medium text-ink'>Account-wide preconditions</p>
				<Link to='/settings/integrations/preconditions' className='text-[13px] text-indigo-500 hover:underline'>
					Manage
				</Link>
			</div>
			{applies ? (
				<>
					<p className='text-[13px] text-muted-foreground'>
						Also checked for this action. It runs on its own only when this action's conditions and{' '}
						{enabled.length > 1 ? `${combinator} of these` : 'this one'} pass.
					</p>
					{enabled.map((rule) => (
						<div key={rule.id} className='flex flex-col gap-0.5 rounded-xl border border-dashed border-gray-300 p-3'>
							<span className='text-sm font-medium text-ink font-mono'>{describe(rule)}</span>
							{rule.description && <span className='text-[13px] text-muted-foreground'>{rule.description}</span>}
						</div>
					))}
				</>
			) : (
				<p className='text-[13px] text-muted-foreground'>
					Not checked for this action: it's low risk and has no preconditions of its own. Add one here and these
					apply too.
				</p>
			)}
		</div>
	);
}

/** Deterministic, code-evaluated checks gating this action's writes — a failure escalates to
 * human approval regardless of the action's own risk level. With provider and tool set to
 * GLOBAL_PRECONDITION_SCOPE it edits the account-wide rules (see ACCOUNT_WIDE_SCOPE). */
export function PreconditionsSection({ provider, tool }: PreconditionsSectionProps) {
	const isGlobal = tool === GLOBAL_PRECONDITION_SCOPE;
	const { data: preconditions, isLoading, isError } = useToolPreconditions(provider, tool);
	const { data: tools } = useToolDefinitions();
	// Account-wide rules shown under an action's own (on the account-wide page they ARE `preconditions`).
	const { data: globalRules } = useToolPreconditions(GLOBAL_PRECONDITION_SCOPE, GLOBAL_PRECONDITION_SCOPE);
	const accountWide = isGlobal ? [] : (globalRules ?? []).filter((rule) => rule.enabled);
	const action = (tools ?? []).find((t) => t.provider.toLowerCase() === provider.toLowerCase() && t.tool === tool);
	const accountWideApplies =
		(preconditions ?? []).some((rule) => rule.enabled) || !runsWithoutApproval(action?.riskLevel);
	// An account-wide rule whose source tool isn't on the account can't be checked, so every
	// action it covers fails closed and waits for approval.
	const uncheckable =
		isGlobal &&
		!!tools &&
		(preconditions ?? []).some(
			(rule) =>
				rule.enabled &&
				!!rule.sourceTool &&
				!tools.some((t) => `${t.provider}:${t.tool}`.toLowerCase() === rule.sourceTool?.toLowerCase())
		);
	const updateMutation = useUpdatePrecondition();
	const deleteMutation = useDeletePrecondition();
	const combinatorMutation = useSetPreconditionCombinator();
	const { can } = usePermissions();
	const canEdit = can('edit', 'integrations');

	const combinator: PreconditionCombinator = preconditions?.[0]?.combinator ?? 'all';
	const setCombinator = async (next: PreconditionCombinator) => {
		try {
			await combinatorMutation.mutateAsync({ provider, tool, combinator: next });
		} catch {
			toast.error('Failed to update how conditions combine');
		}
	};

	const [isFormOpen, setIsFormOpen] = useState(false);
	const [editing, setEditing] = useState<ToolPrecondition | null>(null);

	const openCreate = () => {
		setEditing(null);
		setIsFormOpen(true);
	};
	const openEdit = (rule: ToolPrecondition) => {
		setEditing(rule);
		setIsFormOpen(true);
	};

	const toggleEnabled = async (rule: ToolPrecondition, enabled: boolean) => {
		try {
			await updateMutation.mutateAsync({ id: rule.id, provider, tool, data: { enabled } });
		} catch {
			toast.error('Failed to update precondition');
		}
	};

	const remove = async (rule: ToolPrecondition) => {
		try {
			await deleteMutation.mutateAsync({ id: rule.id, provider, tool });
			toast.success('Precondition removed');
		} catch {
			toast.error('Failed to remove precondition');
		}
	};

	return (
		<section className='flex flex-1 flex-col gap-3'>
			{canEdit && (
				<div className='flex justify-end'>
					<Button variant='outline' className={cn(PILL_BUTTON, 'gap-1')} onClick={openCreate}>
						<Plus size={14} />
						Add precondition
					</Button>
				</div>
			)}

			{uncheckable && (
				<p role='alert' className='rounded-xl border border-amber-300 bg-amber-50 p-3 text-[13px] text-amber-800'>
					Shopify isn't connected, so these can't be checked. Every action they cover waits for approval until
					it's connected or the rules are turned off.
				</p>
			)}

			{isLoading || isError || !preconditions?.length ? (
				// Loading, error and empty states sit in the middle of the tab — unless account-wide rules
				// follow, which a centred empty state would push below the fold.
				<div className={cn('flex items-center justify-center', accountWide.length ? 'py-6' : 'flex-1')}>
					{isLoading ? (
						<Spinner className='size-5 text-gray-700' />
					) : isError ? (
						<p className='text-sm text-red-500'>Failed to load preconditions.</p>
					) : (
						<p className='text-sm text-muted-foreground'>
							{isGlobal
								? 'No account-wide preconditions — actions are checked only against their own.'
								: accountWide.length && accountWideApplies
									? 'No preconditions of its own — this action is still checked against the account-wide ones below.'
									: 'No preconditions configured — this action runs at its default risk level.'}
						</p>
					)}
				</div>
			) : (
				<div className='flex flex-col gap-2'>
					{preconditions.length > 1 && (
						<div className='flex items-center gap-2 text-sm text-ink'>
							<span>Require</span>
							<Select value={combinator} disabled={!canEdit} onValueChange={(v) => setCombinator(v as PreconditionCombinator)}>
								<SelectTrigger size='sm' className='w-[90px]'>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value='all'>all</SelectItem>
									<SelectItem value='any'>any</SelectItem>
								</SelectContent>
							</Select>
							<span>of the following conditions to be met</span>
						</div>
					)}
					{preconditions.map((rule) => (
						<div
							key={rule.id}
							className='flex items-center justify-between gap-3 border border-gray-300 rounded-xl p-3'
						>
							<div className='flex flex-col gap-0.5 min-w-0'>
								<span className='text-sm font-medium text-ink font-mono'>{describe(rule)}</span>
								{rule.description && (
									<span className='text-[13px] text-muted-foreground'>{rule.description}</span>
								)}
							</div>
							<div className='flex items-center gap-2 shrink-0'>
								<Switch
									checked={rule.enabled}
									disabled={!canEdit}
									onCheckedChange={(checked) => toggleEnabled(rule, checked)}
								/>
								{canEdit && (
									<>
										<Button variant='ghost' size='icon' className='size-7' onClick={() => openEdit(rule)}>
											<Pencil size={14} />
										</Button>
										<AlertDialog>
											<AlertDialogTrigger asChild>
												<Button variant='ghost' size='icon' className='size-7 text-red-500 hover:text-red-600'>
													<Trash2 size={14} />
												</Button>
											</AlertDialogTrigger>
											<AlertDialogContent>
												<AlertDialogHeader>
													<AlertDialogTitle>Remove this precondition?</AlertDialogTitle>
													<AlertDialogDescription>
														"{describe(rule)}" will no longer be checked before{' '}
														{isGlobal ? 'actions run' : 'this action runs'}. This can't be undone.
													</AlertDialogDescription>
												</AlertDialogHeader>
												<AlertDialogFooter>
													<AlertDialogCancel>Cancel</AlertDialogCancel>
													<AlertDialogAction onClick={() => remove(rule)}>Remove</AlertDialogAction>
												</AlertDialogFooter>
											</AlertDialogContent>
										</AlertDialog>
									</>
								)}
							</div>
						</div>
					))}
				</div>
			)}

			{accountWide.length > 0 && <AccountWidePreconditions enabled={accountWide} applies={accountWideApplies} />}

			<PreconditionFormDialog
				open={isFormOpen}
				onOpenChange={setIsFormOpen}
				provider={provider}
				tool={tool}
				combinator={combinator}
				editing={editing}
			/>
		</section>
	);
}
